<template>
    <v-menu
        ref="menu"
        v-model="menuOpen"
        :attach="$refs.popper"
        :close-on-content-click="false"
        content-class="menu-rounded"
        transition="popper-scale"
        :disabled="disabled"
        :min-width="pickerWidth"
        :max-width="pickerWidth">
        <template #activator="{ on, attrs }">
            <v-text-field
                v-model="textFieldValue"
                ref="textField"
                :label="label"
                :disabled="disabled"
                :placeholder="placeholder"
                clearable
                class="cursor-text"
                dense
                outlined
                aria-label="time input"
                v-bind="attrs"
                v-on="on"
                :hint="format_hint"
                hide-details="auto">
                <template v-if="!disablePrefixIcon"  v-slot:prepend>
                    <v-icon :color="menuOpen || focused ? 'primary' : ''" @click="menuOpen=!menuOpen;">
                        {{ menuOpen ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}
                    </v-icon>
                </template>
            </v-text-field>

            <portal selector="#app">
                <div
                    ref="popper"
                    class="popper-menu-anchor"
                    :class="{'popper-menu-hide-under-navbar': !popupUsedInModal}"
                >
                </div>
            </portal>
        </template>

        <v-time-picker
            v-if="menuShown"
            v-model="inner_value"
            :format="format"
            :max="max"
            :min="min"
            :use-seconds="useSeconds"
            full-width
            @click:minute="menuOpen = false"
        />
    </v-menu>
</template>


<script>
    import popperPositionedVuetifyMenuMixin from '../../mixins/popperPositionedVuetifyMenuMixin';
    import { Portal } from '@linusborg/vue-simple-portal';
    import VTooltipWrapper from './v-tooltip-wrapper.vue';
    import { formatTime, getTimeFormat } from '../../../utils/time';

    export default {
        components: { VTooltipWrapper, Portal },
        props: {
            'label': String,
            'value': String,
            'disabled': Boolean,
            'min': String,
            'max': String,
            'placeholder':String,
            'id': String,
            'align-right': Boolean,
            'mode': String,
            'picker-family': String,
            'use-seconds': Boolean,
            'hide-format-hint': Boolean,
            'disable-prefix-icon': Boolean,
            'popupUsedInModal': Boolean,
        },
        mixins: [popperPositionedVuetifyMenuMixin],
        data: function () {
            return {
                menuOpen: false,
                expand: false,
                focused: false,
                timepickerMode: Laravel.timepickerMode,
            }
        },
        computed: {
            inner_value: {
                get () {
                    return this.value;
                },
                set (val, oldVal) {
                    if (val !== oldVal) {
                        this.$emit('input', val ?? '');
                    }
                }
            },
            textFieldValue: {
                get() {
                    return this.parseTime(this.value);
                },
                set(val) {
                    this.inner_value = this.parseBackTime(val);
                }
            },
            format_hint: function () {
                if (this.hideFormatHint) {
                    return '';
                }

                let hint = 'HH:MM';
                if (this.useSeconds) {
                    hint = 'HH:MM:SS';
                }
                if (this.format === 'ampm') {
                    return hint + ' am/pm';
                } else {
                    return hint;
                }
            },
            pickerWidth() {
                return this.format === 'ampm' ? '335px' : '305px';
            },
            format() {
                return getTimeFormat(this.timepickerMode);
            }
        },
        watch: {
            expand: function (val) {
                if (this.pickerFamily && val) {
                    EaseEventBus.fire('hide-picker-family-' + this.pickerFamily, this.id);
                }
            },
            id: function () {
                this.init();
            }
        },
        methods: {
            parseTime: function (val) {
                if (!val || !val.split) {
                    return '';
                }

                let split = val.split(':');

                let H = split.length > 0 ? parseInt(split[0]) : undefined;
                let M = split.length > 1 ? parseInt(split[1]) : undefined;
                let S = split.length > 2 ? parseInt(split[2]) : undefined;

                if (isNaN(H) && isNaN(M) && isNaN(S)) {
                    return '';
                }

                H = isNaN(H) ? 0 : H;
                M = isNaN(M) ? 0 : M;
                S = isNaN(S) ? 0 : S;

                return formatTime({
                    h: H,
                    m: M,
                    s: this.useSeconds ? S : undefined
                }, this.format);
            },
            parseBackTime: function (val) {
                if(!val) {
                    return '';
                }

                let ampm = '';
                if (this.format === 'ampm') {
                    ampm = val.substring(val.length-2).toLowerCase();
                    if (ampm !== 'pm' && ampm !== 'am') {
                        return this.value;
                    }
                    val = val.substring(0, val.length-3);
                }
                let split = val.split(':');

                let H = split.length > 0 ? parseInt(split[0]) : 0;
                let M = split.length > 1 ? parseInt(split[1]) : 0;
                let S = split.length > 2 ? parseInt(split[2]) : 0;

                if (isNaN(H) || isNaN(M) || isNaN(S)) {
                    return this.value;
                }
                if ((ampm === 'pm' && H > 12) || (ampm === 'am' && H === 0)) {
                    H = H + 12;
                }
                H = Math.max(Math.min(H, 24), 0);
                M = Math.max(Math.min(M, 59), 0);
                S = Math.max(Math.min(S, 59), 0);
                if (H === 24) {
                    M = 0;
                    S = 0;
                }

                let time = H.toString().padStart(2, "0") + ':' + M.toString().padStart(2, "0");
                if (this.useSeconds) {
                    return time + ':' + S.toString().padStart(2, "0");
                } else {
                    return time;
                }
            },
            init: function () {
                this.menuOpen = false;
                this.expand = false;
            },
            getReferenceEl() {
                return this.$refs.textField.$el;
            },
            getPopperEl() {
                return this.$refs.popper;
            },
            getVMenuEl() {
                return this.$refs.menu.$el;
            }
        },
        created() {
            EaseEventBus.listen('timepickerModeUpdated', timepickerMode => {
                this.timepickerMode = timepickerMode;
            });
        },
        mounted() {
            this.init();

            if (this.mode === 'expand' && this.pickerFamily) {
                EaseEventBus.listen('hide-picker-family-' + this.pickerFamily, (id) => {
                    if (this.id !== id) {
                        this.expand = false;
                    }
                })
            }
        }
    }
</script>

<style>
.v-picker--time .v-picker__title .v-picker__title__btn,
.v-picker--time .v-picker__title span {
    font-size: inherit;
    height: auto;
}
.v-picker--time .v-picker__title .v-time-picker-title__ampm {
    margin: 0;
}

.v-picker--time .v-picker__title {
    padding: 8px 16px;
}

.v-picker--time .v-picker__title .v-time-picker-title {
    justify-content: center;
    font-size: xx-large;
}
</style>
